import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetch_slots, create_booking, pay_booking, resume_payment, verify_payment, fetch_my_bookings, fetch_booking_detail, fetch_vendor_booking_detail, cancel_booking, reschedule_booking, fetch_booking_record } from './api';
import type { CreateBookingRequest, VerifyPaymentRequest } from './types';

const MY_BOOKINGS_KEY = ['my-bookings'] as const;

export function use_slots(service_id: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: ['slots', service_id, date],
    queryFn: () => fetch_slots(service_id as string, date as string),
    enabled: !!service_id && !!date,
  });
}

export function use_create_booking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBookingRequest) => create_booking(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_BOOKINGS_KEY }),
  });
}

export function use_pay_booking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pay_booking(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_BOOKINGS_KEY }),
  });
}

export function use_resume_payment() {
  return useMutation({ mutationFn: (id: string) => resume_payment(id) });
}

export function use_verify_payment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: VerifyPaymentRequest }) => verify_payment(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_BOOKINGS_KEY }),
  });
}

export function use_my_bookings() {
  // Light polling so the active walk-OTP code appears/clears (and status flips)
  // without a manual refresh while a walk is in progress. No websockets (PRD).
  return useQuery({
    queryKey: MY_BOOKINGS_KEY,
    queryFn: fetch_my_bookings,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function use_booking_detail(id: string | null) {
  return useQuery({
    queryKey: ['booking-detail', id],
    queryFn: () => fetch_booking_detail(id as string),
    enabled: !!id,
  });
}

export function use_vendor_booking_detail(id: string | null) {
  return useQuery({
    queryKey: ['vendor-booking-detail', id],
    queryFn: () => fetch_vendor_booking_detail(id as string),
    enabled: !!id,
  });
}

export function use_booking_record(id: string, enabled = true) {
  return useQuery({
    queryKey: ['booking-record', id],
    queryFn: () => fetch_booking_record(id),
    enabled: enabled && id.length > 0,
  });
}

export function use_cancel_booking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => cancel_booking(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_BOOKINGS_KEY }),
  });
}

export function use_reschedule_booking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, slot_start }: { id: string; slot_start: string }) => reschedule_booking(id, slot_start),
    onSuccess: () => qc.invalidateQueries({ queryKey: MY_BOOKINGS_KEY }),
  });
}
